import { Fragment, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Activity,
  Boxes,
  Briefcase,
  Building2,
  CalendarClock,
  CheckCircle,
  ChevronDown,
  Bell,
  Clock,
  CloudDownload,
  Copy,
  FileCode,
  FileSearch,
  FileStack,
  FileText,
  HardDrive,
  Inbox,
  KeyRound,
  Layers,
  Link2,
  LayoutDashboard,
  LayoutGrid,
  List,
  ListChecks,
  ListOrdered,
  Map as MapIcon,
  MapPin,
  MonitorSmartphone,
  PackagePlus,
  Printer,
  RefreshCw,
  Rocket,
  ScrollText,
  Search,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Users,
  Variable,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { usePermissions } from './PermissionsContext'
import {
  isHeading,
  isSection,
  defaultOpenSections,
  sectionForPath,
  visibleNavigation,
  type NavLinkItem,
  type NavSection,
} from './navigation'
import { cn } from '../design/cn'

/**
 * The Relay module's navigation — `Components/Layout/Sidebar.razor`.
 *
 * **It lives in the shell, not in `RelayRoutes`.** The module is deliberately chrome-free so that
 * it renders correctly inside the AboveStore shell as well as this one, and navigation is chrome:
 * a federated host supplies its own. `AppShell` is the standalone host, so the standalone host
 * owns this.
 *
 * Icons come from lucide-react rather than from `design/Icon`. That is the same split `AppShell`'s
 * module bar already uses: `design/Icon` is a transcription of `Components/Shared/Icons/*.razor`
 * for *page* content, where the original shapes matter; the sidebar's are chrome, and inventing
 * eleven new `d` attributes to avoid a library the shell already imports would be the worse trade.
 */

const ICONS: Record<string, LucideIcon> = {
  Activity,
  Boxes,
  Briefcase,
  Building2,
  CalendarClock,
  CheckCircle,
  Bell,
  Clock,
  CloudDownload,
  Copy,
  FileCode,
  FileSearch,
  FileStack,
  FileText,
  HardDrive,
  Inbox,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Map: MapIcon,
  MapPin,
  MonitorSmartphone,
  PackagePlus,
  Printer,
  RefreshCw,
  Rocket,
  ScrollText,
  Search,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Users,
  Variable,
  Zap,
}

export function NavIcon({ name }: { name: string }) {
  // Settings is the fallback rather than nothing: a missing icon should leave the row aligned with
  // its neighbours, not shift every label in the section by 15px.
  const Icon = ICONS[name] ?? Settings
  return <Icon size={16} aria-hidden="true" className="shrink-0" />
}

const LINK_BASE =
  'flex items-center gap-sm rounded-md px-md py-sm text-sm transition-colors w-full text-left'

function linkClass({ isActive }: { isActive: boolean }, nested = false): string {
  return cn(
    LINK_BASE,
    nested && 'pl-xl text-xs',
    isActive
      ? 'bg-sidebar-active text-sidebar-active-fg font-medium'
      : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-fg-hover',
  )
}

function Item({ item, nested }: { item: NavLinkItem; nested?: boolean }) {
  const label = (
    <>
      <NavIcon name={item.icon} />
      <span className="truncate">{item.label}</span>
    </>
  )

  return (
    <li>
      <NavLink to={item.to} end={item.exact} className={(state) => linkClass(state, nested)}>
        {label}
      </NavLink>
    </li>
  )
}

function Section({
  section,
  expanded,
  onToggle,
}: {
  section: NavSection
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`nav-section-${section.id}`}
        className={cn(
          LINK_BASE,
          'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-fg-hover',
        )}
      >
        <NavIcon name={section.icon} />
        <span className="flex-1 truncate">{section.label}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={cn('shrink-0 transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {/* Unmounted rather than hidden: a collapsed section's links should not be reachable by tab. */}
      {expanded ? (
        <ul id={`nav-section-${section.id}`} className="mt-xs flex flex-col gap-xs">
          {section.items.map((item, i) => (
            <Fragment key={item.to}>
              {/* A group's heading before its first link: it labels, so it is not itself a link. */}
              {item.group && item.group !== section.items[i - 1]?.group ? (
                <li
                  aria-hidden="true"
                  className="px-md pb-xs pl-xl pt-sm text-[10.5px] font-medium uppercase tracking-wider text-fg-muted"
                >
                  {item.group}
                </li>
              ) : null}
              <Item item={item} nested />
            </Fragment>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export function Sidebar() {
  const { hasPermission, hasBrandWidePermission, isLoading } = usePermissions()
  const { pathname } = useLocation()

  const [expanded, setExpanded] = useState<string[]>(() => {
    const open = sectionForPath(pathname)
    const always = defaultOpenSections()
    return open && !always.includes(open) ? [...always, open] : always
  })

  // Open the branch a deep link lands in. Without this, arriving at /jobs/runs from a bookmark
  // shows a collapsed sidebar with nothing indicating where in it you are.
  useEffect(() => {
    const open = sectionForPath(pathname)
    if (open) setExpanded((current) => (current.includes(open) ? current : [...current, open]))
  }, [pathname])

  const toggle = (id: string) =>
    setExpanded((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    )

  // Nothing until the profile arrives. Rendering the ungated entries first and the rest a moment
  // later makes the navigation appear to grow while being read, and it is the kind of flicker that
  // gets clicked through by accident.
  const entries = isLoading
    ? []
    : visibleNavigation(hasPermission, undefined, hasBrandWidePermission ?? hasPermission)

  return (
    <nav
      aria-label="Relay"
      className="w-sidebar shrink-0 overflow-y-auto border-r border-line bg-sidebar-bg p-sm"
    >
      <ul className="flex flex-col gap-xs">
        {entries.map((entry) =>
          isHeading(entry) ? (
            <li
              key={`heading-${entry.heading}`}
              aria-hidden="true"
              className="mt-md px-md pb-xs pt-sm text-xs font-semibold uppercase tracking-wider text-fg-muted"
            >
              {entry.heading}
            </li>
          ) : isSection(entry) ? (
            <Section
              key={entry.id}
              section={entry}
              expanded={expanded.includes(entry.id)}
              onToggle={() => toggle(entry.id)}
            />
          ) : (
            <Item key={entry.to} item={entry} />
          ),
        )}
      </ul>
    </nav>
  )
}
